import { useEffect, useRef, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, TextField, Button, Typography, Chip, Stack, Autocomplete } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SaveIcon from '@mui/icons-material/Save';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import type { VisitaFormulario } from '../hooks/useVisitas';
import { FotoVisita } from './FotoVisita';

// AIDEV-NOTE: Tags predefinidas para visitas — novas tags podem ser criadas via freeSolo
const TAG_OPTIONS = ['prospecção', 'manutenção', 'cobrança', 'entrega', 'negociação', 'reclamação'];

interface FormularioVisitaProps {
  visitaId: string;
  initialValues?: VisitaFormulario;
  readOnly?: boolean;
  onSaved?: () => void;
}

interface FormValues {
  observacoes: string;
  objetivo: string;
  resultado: string;
  proxima_acao: string;
  tags: string[];
  fotos: string[];
}

// AIDEV-NOTE: Auto-save a cada 30s salva localmente via Dexie (futuro), e "Salvar" envia para API
export function FormularioVisita({
  visitaId,
  initialValues,
  readOnly = false,
  onSaved
}: FormularioVisitaProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      observacoes: initialValues?.observacoes ?? '',
      objetivo: initialValues?.objetivo ?? '',
      resultado: initialValues?.resultado ?? '',
      proxima_acao: initialValues?.proxima_acao ?? '',
      tags: initialValues?.tags ?? [],
      fotos: initialValues?.fotos ?? []
    }
  });

  const autoSaveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isSyncedRef = useRef(!!initialValues);
  const formValues = watch();

  const saveLocally = useCallback(() => {
    // AIDEV-TODO: Implementar persistência local via Dexie para auto-save offline
    try {
      localStorage.setItem(`visita_form_${visitaId}`, JSON.stringify(formValues));
      isSyncedRef.current = false;
    } catch {
      // Falha silenciosa no auto-save local — não deve bloquear o usuário
    }
  }, [visitaId, formValues]);

  // AIDEV-NOTE: Auto-save a cada 30 segundos — salva no localStorage (migrar para Dexie)
  useEffect(() => {
    if (readOnly) return;

    autoSaveTimerRef.current = setInterval(() => {
      if (isDirty) {
        saveLocally();
      }
    }, 30_000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [readOnly, isDirty, saveLocally]);

  const onSubmit = async (data: FormValues) => {
    try {
      const res = await fetch(`/api/visitas/${visitaId}/formulario`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error('Falha ao salvar formulário');
      isSyncedRef.current = true;
      localStorage.removeItem(`visita_form_${visitaId}`);
      onSaved?.();
    } catch (error) {
      saveLocally();
      throw error;
    }
  };

  // Modo leitura: exibe dados como Typography
  if (readOnly) {
    return (
      <Box>
        <Stack spacing={2}>
          {initialValues?.objetivo && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Objetivo
              </Typography>
              <Typography variant="body2">{initialValues.objetivo}</Typography>
            </Box>
          )}
          {initialValues?.resultado && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Resultado
              </Typography>
              <Typography variant="body2">{initialValues.resultado}</Typography>
            </Box>
          )}
          {initialValues?.proxima_acao && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Proxima Acao
              </Typography>
              <Typography variant="body2">{initialValues.proxima_acao}</Typography>
            </Box>
          )}
          {initialValues?.observacoes && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Observacoes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {initialValues.observacoes}
              </Typography>
            </Box>
          )}
          {initialValues?.tags && initialValues.tags.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Tags
              </Typography>
              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
                {initialValues.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
          {/* AIDEV-NOTE: Fotos da visita em modo leitura */}
          {initialValues?.fotos && initialValues.fotos.length > 0 && (
            <Box>
              <FotoVisita fotos={initialValues.fotos} onChange={() => {}} disabled />
            </Box>
          )}
          {!initialValues?.objetivo && !initialValues?.resultado && !initialValues?.observacoes && (
            <Typography variant="body2" color="text.secondary">
              Nenhum dado de formulario registrado.
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        {/* Indicador de status de sincronização */}
        <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
          {isSyncedRef.current ? (
            <>
              <CloudDoneIcon fontSize="small" color="success" />
              <Typography variant="caption" color="success.main">
                Sincronizado
              </Typography>
            </>
          ) : (
            <>
              <PhoneAndroidIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                Salvo localmente
              </Typography>
            </>
          )}
        </Box>

        <Controller
          name="objetivo"
          control={control}
          rules={{ maxLength: { value: 500, message: 'Maximo 500 caracteres' } }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Objetivo"
              placeholder="O que pretendia alcancar?"
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? `${field.value.length}/500`}
              inputProps={{ maxLength: 500 }}
            />
          )}
        />

        <Controller
          name="resultado"
          control={control}
          rules={{ maxLength: { value: 500, message: 'Maximo 500 caracteres' } }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Resultado"
              placeholder="O que foi alcancado?"
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? `${field.value.length}/500`}
              inputProps={{ maxLength: 500 }}
            />
          )}
        />

        <Controller
          name="proxima_acao"
          control={control}
          rules={{ maxLength: { value: 500, message: 'Maximo 500 caracteres' } }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Proxima Acao"
              placeholder="Proximo passo a tomar"
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? `${field.value.length}/500`}
              inputProps={{ maxLength: 500 }}
            />
          )}
        />

        <Controller
          name="observacoes"
          control={control}
          rules={{ maxLength: { value: 2000, message: 'Maximo 2000 caracteres' } }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Observacoes"
              multiline
              rows={4}
              size="small"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? `${field.value.length}/2000`}
              inputProps={{ maxLength: 2000 }}
            />
          )}
        />

        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              freeSolo
              options={TAG_OPTIONS}
              value={field.value}
              onChange={(_e, newValue) => field.onChange(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key} label={option} size="small" {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Tags" size="small" placeholder="Adicionar tag..." />
              )}
            />
          )}
        />

        {/* AIDEV-NOTE: Componente de fotos integrado ao formulario de visita */}
        <Controller
          name="fotos"
          control={control}
          render={({ field }) => <FotoVisita fotos={field.value} onChange={field.onChange} />}
        />

        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isSubmitting}
          size="small"
        >
          {isSubmitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </Stack>
    </Box>
  );
}
